/**
 * EnhancedComponent - Universal wrapper for registry-based components
 * Handles rendering of sections registered via EnhancedSectionRegistry
 */

import { devError } from '../../../../../utils/devLog';
import React, { useEffect } from 'react';
import { EnhancedSectionConfig } from '../registry/EnhancedSectionRegistry';
import { JatakaResponse } from '../../../../../models/api';
import { UniversalDivisionalToggle } from './UniversalDivisionalToggle';

interface EnhancedComponentProps {
    config: EnhancedSectionConfig;
    apiResponse: JatakaResponse;
    divisionalSettings?: any;
    [key: string]: any; // Additional props
}

/**
 * EnhancedComponent - Renders a section based on its registry configuration
 *
 * Features:
 * - Type-based rendering (toggle, table, default)
 * - Lazy loading support
 * - Conditional rendering
 * - Event lifecycle hooks
 */
export const EnhancedComponent: React.FC<EnhancedComponentProps> = ({
    config,
    apiResponse,
    divisionalSettings,
    ...additionalProps
}) => {
    // Fire onMount event
    useEffect(() => {
        if (config.events?.onMount) {
            config.events.onMount(apiResponse);
        }
    }, [config, apiResponse]);

    // Check condition
    if (config.condition && !config.condition(apiResponse)) {
        return null;
    }

    // Handle toggle type - render UniversalDivisionalToggle
    if (config.type === 'toggle') {
        const itemId = config.componentProps?.itemId || config.chakra?.id;

        if (!itemId) {
            devError(`❌ EnhancedComponent: Toggle "${config.id}" missing itemId`);
            return null;
        }

        // Get external settings based on itemId
        const getExternalSettings = () => {
            if (!divisionalSettings) return undefined;

            // Map itemId to settings key
            const settingsMap: Record<string, any> = {
                'muhurta-chakra': divisionalSettings.muhurta,
                'nakshatra-chakra': divisionalSettings.nakshatra,
                'd7-chara-karaka': divisionalSettings.d7,
                'd8-ashtamsha': divisionalSettings.d8,
                'd12-dwadashamsha': divisionalSettings.d12,
                'd20-vimsamsha-guruvadi': divisionalSettings.d20,
                'd24-divisions': divisionalSettings.d24,
                'd30-divisions': divisionalSettings.d30,
                'd40-divisions': divisionalSettings.d40,
                'd60-karana-shashtyamsha': divisionalSettings.d60
            };

            return settingsMap[itemId];
        };

        return (
            <UniversalDivisionalToggle
                toggleLabel={config.componentProps?.toggleLabel || config.label}
                colorScheme={config.componentProps?.colorScheme || 'blue'}
                itemId={itemId}
                {...config.componentProps}
                apiResponse={apiResponse}
                externalSettings={getExternalSettings()}
                {...additionalProps}
            />
        );
    }

    // Handle table type - render component directly
    if (config.type === 'table') {
        const Component = config.component;

        return (
            <Component
                apiResponse={apiResponse}
                {...config.componentProps}
                {...additionalProps}
            />
        );
    }

    // Handle default type - render component with full props
    const Component = config.component;

    return (
        <Component
            apiResponse={apiResponse}
            {...config.componentProps}
            {...additionalProps}
        />
    );
};

export default EnhancedComponent;
